/**
 * G14 Quick Result Component
 *
 * Displays G14 calculator results with traffic light recommendation,
 * cost comparison, savings percentage, and zone distribution.
 */

import {
  Card,
  Group,
  Stack,
  Text,
  Title,
  ThemeIcon,
  Progress,
  Alert,
  SimpleGrid,
  Divider,
} from '@mantine/core';
import {
  IconCheck,
  IconAlertTriangle,
  IconX,
  IconInfoCircle,
} from '@tabler/icons-react';
import type { G14QuickCalcResult } from '../hooks/useG14QuickCalc';

interface G14QuickResultProps {
  result: G14QuickCalcResult;
  comparisonTariffName: string;
}

const RECOMMENDATION_CONFIG = {
  green: {
    color: 'ecoGreen.0' as const,
    borderColor: 'ecoGreen.3' as const,
    icon: IconCheck,
    iconColor: 'green' as const,
  },
  yellow: {
    color: 'yellow.0' as const,
    borderColor: 'yellow.3' as const,
    icon: IconAlertTriangle,
    iconColor: 'yellow' as const,
  },
  red: {
    color: 'red.0' as const,
    borderColor: 'red.3' as const,
    icon: IconX,
    iconColor: 'red' as const,
  },
};

const ZONE_COLORS: Record<string, string> = {
  GREEN: '#22c55e',
  LIGHT_GREEN: '#84cc16',
  YELLOW: '#eab308',
  RED: '#ef4444',
};

const ZONE_LABELS: Record<string, string> = {
  GREEN: 'Zielona',
  LIGHT_GREEN: 'Jasnozielona',
  YELLOW: 'Żółta',
  RED: 'Czerwona',
};

export function G14QuickResult({ result, comparisonTariffName }: G14QuickResultProps) {
  const config = RECOMMENDATION_CONFIG[result.recommendation];
  const Icon = config.icon;

  return (
    <Stack gap="md">
      {/* Main Result Card */}
      <Card
        bg={config.color}
        radius="md"
        p="lg"
        withBorder
        style={{ borderColor: `var(--mantine-color-${config.borderColor})` }}
      >
        <Group align="flex-start" gap="md">
          <ThemeIcon size="lg" color={config.iconColor} variant="light" radius="xl">
            <Icon size={20} />
          </ThemeIcon>
          <div style={{ flex: 1 }}>
            <Title order={3}>{result.recommendationText}</Title>
          </div>
        </Group>

        <Divider my="md" />

        {/* Cost Comparison */}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Card withBorder p="sm" radius="sm">
            <Text size="xs" c="dimmed" tt="uppercase">
              {comparisonTariffName}
            </Text>
            <Text size="xl" fw={700}>
              {result.monthlyComparisonCost.toFixed(0)} zł
              <Text span size="sm" c="dimmed" fw={400}>/mies</Text>
            </Text>
          </Card>
          <Card withBorder p="sm" radius="sm">
            <Text size="xs" c="dimmed" tt="uppercase">
              Taryfa G14
            </Text>
            <Text size="xl" fw={700} c={result.recommendation === 'red' ? 'red' : undefined}>
              {result.monthlyG14Cost.toFixed(0)} zł
              <Text span size="sm" c="dimmed" fw={400}>/mies</Text>
            </Text>
          </Card>
        </SimpleGrid>

        {/* Savings */}
        <Stack gap="xs" mt="md">
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              {result.annualSavings >= 0 ? 'Potencjalna oszczędność' : 'Dodatkowy koszt'}
            </Text>
            <Text
              size="lg"
              fw={700}
              c={result.annualSavings >= 0 ? 'green.7' : 'red.7'}
            >
              {Math.abs(result.annualSavings)} zł/rok
            </Text>
          </Group>
          {result.annualSavings > 0 && (
            <Progress
              value={Math.min(Math.abs(result.savingsPercent), 100)}
              color={result.recommendation === 'green' ? 'green' : 'yellow'}
              size="sm"
              radius="xl"
            />
          )}
        </Stack>
      </Card>

      {/* Zone Distribution */}
      {result.zoneDistribution && Object.keys(result.zoneDistribution).length > 0 && (
        <Card withBorder radius="md" p="md">
          <Text size="sm" fw={500} mb="sm">Udział stref cenowych</Text>
          <Stack gap="xs">
            {Object.entries(result.zoneDistribution)
              .sort(([, a], [, b]) => b - a)
              .map(([zone, fraction]) => (
                <Group key={zone} justify="space-between" gap="xs">
                  <Group gap="xs">
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: ZONE_COLORS[zone] ?? '#999',
                      }}
                    />
                    <Text size="sm">{ZONE_LABELS[zone] ?? zone}</Text>
                  </Group>
                  <Text size="sm" fw={500}>{(fraction * 100).toFixed(0)}%</Text>
                </Group>
              ))}
          </Stack>
        </Card>
      )}

      {/* Disclaimer */}
      <Alert
        icon={<IconInfoCircle size={16} />}
        color="gray"
        variant="light"
        radius="md"
      >
        <Text size="xs">
          Szacunek na podstawie średnich cen RCE i uproszczonego profilu zużycia.
          Rzeczywiste koszty zależą od godzinowego zużycia prądu.
          Dla dokładniejszej analizy użyj kalkulatora zaawansowanego z własnym profilem zużycia.
        </Text>
      </Alert>
    </Stack>
  );
}
